import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowUpRight, Compass, Target, Star } from "lucide-react"
import { useSettings } from "@/hooks/useSettings"
import { useTeam } from "@/hooks/useTeam"
import { useCompanyValues } from "@/hooks/useCompanyValues"
import { useStats } from "@/hooks/useStats"
import { useTestimonials } from "@/hooks/useTestimonials"
import { AppIcon } from "@/lib/icon"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/about")({
  component: About,
  head: () =>
    pageMeta({
      title: "About — Nexovia IT",
      description: "Learn about Nexovia IT, our mission and the team behind the software.",
      path: "/about",
    }),
})

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("")
}

function About() {
  const { data: settings, isLoading, isError } = useSettings()
  const { data: team, isLoading: teamLoading, isError: teamError } = useTeam()
  const { data: values, isLoading: valuesLoading, isError: valuesError } = useCompanyValues()
  const { data: stats, isLoading: statsLoading, isError: statsError } = useStats()
  const { data: testimonials, isLoading: testimonialsLoading, isError: testimonialsError } = useTestimonials()

  const activeTeam = (team ?? []).filter((m) => m.is_active).slice(0, 4)
  const activeValues = (values ?? []).filter((v) => v.is_active)
  const activeStats = (stats ?? []).filter((s) => s.is_active)
  const activeTestimonials = (testimonials ?? []).filter((t) => t.is_active).slice(0, 3)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> About Us
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl md:text-7xl font-bold text-balance">
            The team behind <span className="text-rust">{settings?.site_name ?? "Nexovia IT"}</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            {settings?.tagline ??
              "We're a global team of engineers, designers and strategists. We build the systems behind fast-growing companies — the ones you use every day, and the ones you'll use next year."}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Work with us <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/team" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Meet the team
            </Link>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      {isLoading ? (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <SectionSkeleton rows={2} className="md:grid-cols-2" />
        </section>
      ) : isError ? (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <ErrorState message="Couldn't load the about content right now." />
        </section>
      ) : settings?.mission_statement || settings?.vision_statement ? (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Our purpose</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold">Mission &amp; Vision</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {settings?.mission_statement && (
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 md:p-10 transition hover:-translate-y-1 hover:shadow-card">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-ink text-cream">
                  <Target className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-2xl font-bold">Our Mission</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{settings.mission_statement}</p>
              </div>
            )}
            {settings?.vision_statement && (
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 md:p-10 transition hover:-translate-y-1 hover:shadow-card">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-accent text-cream">
                  <Compass className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-2xl font-bold">Our Vision</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{settings.vision_statement}</p>
              </div>
            )}
          </div>
        </section>
      ) : null}

      {/* ABOUT CONTENT */}
      <section className="border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Our story</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-balance">How We Got Here.</h2>
          </div>
          <div className="mt-14 rounded-2xl border border-border bg-card p-8 md:p-12">
            {isLoading ? (
              <div className="space-y-3">
                <div className="h-4 w-full animate-pulse rounded bg-secondary" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-secondary" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-secondary" />
              </div>
            ) : isError ? (
              <ErrorState message="Couldn't load the about content right now." />
            ) : settings?.about_content ? (
              <div
                className="prose prose-neutral max-w-none prose-headings:font-display prose-a:text-rust"
                dangerouslySetInnerHTML={{ __html: settings.about_content }}
              />
            ) : (
              <p className="text-center text-muted-foreground">Content coming soon.</p>
            )}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section>
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Our values</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold">What Drives Us</h2>
          </div>
          <div className="mt-14">
            {valuesLoading ? (
              <SectionSkeleton rows={3} />
            ) : valuesError ? (
              <ErrorState message="Couldn't load our values right now." />
            ) : activeValues.length === 0 ? (
              <p className="text-center text-muted-foreground">Content coming soon.</p>
            ) : (
              <div className="grid gap-6 md:grid-cols-3">
                {activeValues.map((v) => {
                  return (
                    <div
                      key={v.id}
                      className="group rounded-2xl border border-border bg-card p-8 transition hover:-translate-y-1 hover:shadow-card"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-ink text-cream group-hover:bg-gradient-accent transition">
                        <AppIcon name={v.icon} className="h-5 w-5" />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold">{v.title}</h3>
                      {v.description && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.description}</p>}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* STATS */}
      {!statsLoading && !statsError && activeStats.length > 0 && (
        <section className="border-y border-border bg-ink text-cream">
          <div
            className="mx-auto grid max-w-7xl gap-px bg-cream/10"
            style={{ gridTemplateColumns: `repeat(${Math.min(activeStats.length, 4)}, minmax(0, 1fr))` }}
          >
            {activeStats.map((s) => (
              <div key={s.id} className="bg-ink px-6 py-12 text-center">
                <div className="font-display text-4xl md:text-5xl text-rust-glow">{s.value}</div>
                <div className="mt-2 text-sm text-cream/70">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TEAM */}
      <section className="bg-ink text-cream">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-display text-sm uppercase tracking-widest text-rust-glow">Our People</h2>
              <h3 className="mt-3 text-3xl md:text-4xl font-bold">Meet the team.</h3>
            </div>
            <Link to="/team" className="inline-flex items-center gap-2 text-sm font-semibold text-cream hover:text-rust-glow">
              Full team <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10">
            {teamLoading ? (
              <SectionSkeleton rows={4} className="md:grid-cols-4" />
            ) : teamError ? (
              <ErrorState message="Couldn't load the team right now." />
            ) : activeTeam.length === 0 ? (
              <p className="text-cream/60">Team profiles coming soon.</p>
            ) : (
              <div className="grid gap-6 md:grid-cols-4">
                {activeTeam.map((m) => (
                  <div key={m.id} className="rounded-2xl border border-cream/10 bg-cream/5 p-6 text-center transition hover:-translate-y-1 hover:bg-cream/10">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name}
                        width={80}
                        height={80}
                        className="mx-auto h-20 w-20 rounded-full object-cover object-top"
                      />
                    ) : (
                      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-cream/10 font-display text-lg text-cream/80">
                        {initials(m.name)}
                      </div>
                    )}
                    <p className="mt-4 font-semibold">{m.name}</p>
                    <p className="text-xs text-cream/60">{m.designation}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {!testimonialsLoading && !testimonialsError && activeTestimonials.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Client stories</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold">What Clients Say</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {activeTestimonials.map((t) => (
              <div key={t.id} className="rounded-2xl border border-border bg-card p-8">
                <div className="flex gap-0.5 text-rust">
                  {Array.from({ length: Math.max(0, Math.min(5, t.rating)) }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-6 flex items-center gap-3">
                  {t.avatar_url ? (
                    <img
                      src={t.avatar_url}
                      alt={t.client_name}
                      width={40}
                      height={40}
                      className="h-10 w-10 rounded-full object-cover object-top"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-secondary" />
                  )}
                  <div>
                    <p className="text-sm font-semibold">{t.client_name}</p>
                    {t.client_role && <p className="text-xs text-muted-foreground">{t.client_role}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 py-24 md:py-28">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Work with us</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Let's Build Something Great.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Whether you have a clear brief or just an idea, our team is ready to help you ship it.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Work with us <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Explore our services
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
